import React, { useCallback, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { UseEditorOptions } from '@wix/da-editor-tiptap/pkg/hooks/useEditor';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import DescriptionEditor from '@wix/da-shared-react/pkg/DescriptionEditor';
import { QuestionFieldName } from '../../types';
import { EditorTiptapContentReadyHandler } from '@wix/da-shared-react/pkg/DescriptionEditor/DescriptionEditor';
import { EditorApi } from '@wix/da-editor-tiptap/pkg/components/Editor/types';

interface Props {}

const QuestionField: React.FC<Props> = () => {
  const { t } = useTranslation();
  const id = useId();
  const { control, errors, setValue, getValues } = useFormContext();
  const {
    field: { value },
  } = useController({
    name: QuestionFieldName.QUESTION,
    control,
  });
  const error = errors[QuestionFieldName.QUESTION];

  // As generating JSON is expensive for every typing event, we lazily resolve
  // the editors save state on submit rather than on typing.
  const lazyEditorStateValueResolver = useRef<() => string>(undefined);
  const handleInitialContentReady: EditorTiptapContentReadyHandler =
    useCallback(
      (api: EditorApi) => {
        lazyEditorStateValueResolver.current = () => {
          return JSON.stringify(api.getStateForSave());
        };
        setValue(
          QuestionFieldName.QUESTION,
          lazyEditorStateValueResolver.current
        );
      },
      [setValue]
    );

  const handleChange: UseEditorOptions['onEditorChange'] = useCallback(
    editorState =>
      setValue(
        QuestionFieldName.QUESTION,
        getValues(QuestionFieldName.QUESTION),
        {
          shouldDirty: true,
        }
      ),
    [getValues, setValue]
  );

  return (
    <FormElement
      inputId={id}
      label={
        <InputLabelText isRequired>
          {t('customExclusivePopup.questionnaire.form.question.label')}
        </InputLabelText>
      }
    >
      <DescriptionEditor
        placeholder={t(
          'customExclusivePopup.questionnaire.form.question.placeholder'
        )}
        initialContent={value}
        onInitialContentReady={handleInitialContentReady}
        onChange={handleChange}
      />
      {error && <InputErrorMessage>{error.message}</InputErrorMessage>}
    </FormElement>
  );
};

export default QuestionField;
